import { useState } from 'react';
import { formaterDate } from '../lib/format.js';
import { ElementForm } from './ElementForm.jsx';
import {
  ajouterElement,
  modifierElement,
  supprimerElement,
  ajouterJournee,
  supprimerJournee,
  regenererJournee,
} from '../lib/trips.js';

const LIBELLES_CATEGORIE = {
  visite: 'Visite',
  repas: 'Repas',
  transport: 'Transport',
  hebergement: 'Hébergement',
  loisir: 'Loisir',
  ville: 'Ville',
  nature: 'Nature',
  musee: 'Musée',
  activite: 'Activité',
  gastronomie: 'Gastronomie',
  autre: 'Autre',
};

/** Un élément affiché, avec ses actions et sa confirmation de suppression. */
function Element({
  item,
  mode,
  lectureSeule,
  enEdition,
  onEditer,
  onFermer,
  onEnregistrer,
  onSupprimer,
}) {
  const [confirmation, setConfirmation] = useState(false);
  const [suppression, setSuppression] = useState(false);
  const [erreur, setErreur] = useState(null);

  if (enEdition) {
    return (
      <li className="element element--edition">
        <ElementForm
          element={item}
          mode={mode}
          libelleAction="Enregistrer"
          onSubmit={onEnregistrer}
          onCancel={onFermer}
        />
      </li>
    );
  }

  const categorie = LIBELLES_CATEGORIE[item.category] ?? item.category;

  return (
    <li className="element">
      {mode !== 'suggestions_only' && (
        /* L'heure est en colonne : l'œil suit la journée sans lire les titres. */
        <span className="element__heure">{item.time ?? '—'}</span>
      )}

      <div className="element__corps">
        <span className={`element__categorie categorie--${item.category}`}>{categorie}</span>

        <p className="element__titre">{item.title}</p>
        {item.description && <p className="element__description">{item.description}</p>}

        {/* D'où vient l'étape : ce qui a été proposé se distingue de ce qu'on a
            ajouté soi-même, sans quoi on ne sait plus quoi relire. */}
        {item.source && (
          <span className={`element__origine element__origine--${item.source}`}>
            {item.source === 'ai' ? 'Suggéré par l’IA' : 'Ajouté par vous'}
          </span>
        )}

        {erreur && (
          <p className="erreur" role="alert">
            {erreur}
          </p>
        )}

        {lectureSeule ? null : confirmation ? (
          <p className="element__actions" role="alertdialog" aria-label="Confirmer la suppression">
            <span className="element__question">Supprimer cette étape ?</span>
            <button
              className="lien-action lien-action--danger"
              type="button"
              disabled={suppression}
              onClick={async () => {
                setErreur(null);
                setSuppression(true);
                try {
                  await onSupprimer();
                } catch (echec) {
                  setErreur(echec.message);
                  setSuppression(false);
                  setConfirmation(false);
                }
              }}
            >
              {suppression ? 'Suppression…' : 'Oui, supprimer'}
            </button>
            <button className="lien-action" type="button" onClick={() => setConfirmation(false)}>
              Annuler
            </button>
          </p>
        ) : (
          <p className="element__actions">
            <button className="lien-action" type="button" onClick={onEditer}>
              Modifier
            </button>
            <button
              className="lien-action lien-action--danger"
              type="button"
              onClick={() => setConfirmation(true)}
            >
              Supprimer
            </button>
          </p>
        )}
      </div>
    </li>
  );
}

/**
 * Bouton de suppression d'une journée.
 *
 * Sans état local, volontairement : les journées sont identifiées par leur
 * numéro, et supprimer la deuxième fait remonter la troisième à sa place.
 * React voit la même clé, réutilise le composant, et lui applique l'état de la
 * journée disparue — le jour suivant affichait « Suppression… » sans que rien
 * ne se passe. La confirmation vit donc dans le parent, qui n'en garde qu'une
 * à la fois.
 */
function SupprimerJournee({ jour, confirme, onDemander, onAnnuler, onSupprimer, envoi }) {
  if (!confirme) {
    return (
      <button className="lien-action lien-action--danger" type="button" onClick={onDemander}>
        Supprimer ce jour
      </button>
    );
  }

  return (
    <span
      className="journee__confirmation"
      role="alertdialog"
      aria-label="Confirmer la suppression du jour"
    >
      <span className="element__question">
        {jour.items.length > 0
          ? `Supprimer ce jour et ses ${jour.items.length} étape${jour.items.length > 1 ? 's' : ''} ?`
          : 'Supprimer ce jour ?'}
        {' Le voyage sera raccourci d’un jour.'}
      </span>
      <button
        className="lien-action lien-action--danger"
        type="button"
        disabled={envoi}
        onClick={onSupprimer}
      >
        {envoi ? 'Suppression…' : 'Oui, supprimer'}
      </button>
      <button className="lien-action" type="button" onClick={onAnnuler}>
        Annuler
      </button>
    </span>
  );
}

export function Itineraire({
  itineraire,
  tripId,
  onChange,
  lectureSeule = false,
  journeeEnGeneration = null,
}) {
  // Identifiant de l'élément en cours d'édition, ou « ajout:<journée> » quand
  // c'est un ajout. Une seule édition à la fois : deux formulaires ouverts sur
  // le même itinéraire donneraient deux états concurrents.
  const [ouvert, setOuvert] = useState(null);
  // Journée dont la suppression est en attente de confirmation, et journée en
  // cours de suppression. Tenus ici plutôt que dans chaque journée : les
  // numéros se décalent, et un état local suivrait la mauvaise.
  const [journeeAConfirmer, setJourneeAConfirmer] = useState(null);
  const [journeeEnSuppression, setJourneeEnSuppression] = useState(null);
  const [erreurJournee, setErreurJournee] = useState(null);
  const [jourChoisi, setJourChoisi] = useState(1);
  const [erreurRegeneration, setErreurRegeneration] = useState(null);

  const suggestions = itineraire.mode === 'suggestions_only';
  const fermer = () => setOuvert(null);

  const enregistrerAjout = (dayIndex) => async (donnees) => {
    onChange(await ajouterElement(tripId, { ...donnees, ...(dayIndex ? { dayIndex } : {}) }));
    fermer();
  };

  const enregistrerModification = (itemId) => async (donnees) => {
    onChange(await modifierElement(tripId, itemId, donnees));
    fermer();
  };

  const retirer = (itemId) => async () => {
    onChange(await supprimerElement(tripId, itemId));
    fermer();
  };

  const retirerJournee = (dayIndex) => async () => {
    setErreurJournee(null);
    setJourneeEnSuppression(dayIndex);
    try {
      onChange(await supprimerJournee(tripId, dayIndex));
      setJourneeAConfirmer(null);
      fermer();
    } catch (echec) {
      setErreurJournee(echec.message);
      setJourneeAConfirmer(null);
    } finally {
      setJourneeEnSuppression(null);
    }
  };

  const proprietesElement = (item) => ({
    item,
    mode: itineraire.mode,
    lectureSeule,
    enEdition: ouvert === item.id,
    onEditer: () => setOuvert(item.id),
    onFermer: fermer,
    onEnregistrer: enregistrerModification(item.id),
    onSupprimer: retirer(item.id),
  });

  if (suggestions) {
    return (
      <section className="itineraire">
        <p className="aide aide--haut">
          Les dates ne sont pas fixées : voici des idées, classées de la plus
          incontournable à la plus optionnelle.
        </p>

        <ul className="suggestions">
          {itineraire.suggestions.map((suggestion) => (
            <Element key={suggestion.id} {...proprietesElement(suggestion)} />
          ))}
        </ul>

        {lectureSeule ? null : ouvert === 'ajout' ? (
          <div className="ajout">
            <ElementForm
              mode={itineraire.mode}
              libelleAction="Ajouter"
              onSubmit={enregistrerAjout(null)}
              onCancel={fermer}
            />
          </div>
        ) : (
          <button
            className="bouton bouton--discret bouton--auto bouton--ajout"
            type="button"
            onClick={() => setOuvert('ajout')}
          >
            Ajouter une suggestion
          </button>
        )}
      </section>
    );
  }

  // La journée choisie peut avoir disparu — suppression, régénération plus
  // courte : on retombe sur la première plutôt que d'afficher le vide.
  const jour = itineraire.days.find((j) => j.dayIndex === jourChoisi) ?? itineraire.days[0];
  if (!jour) return null;

  return (
    <section className="itineraire">
      {/* Une journée à la fois : sur un séjour de dix jours, tout empiler
          obligeait à faire défiler des écrans entiers pour atteindre la fin. */}
      <ul className="jours">
        {itineraire.days.map((j) => (
          <li key={j.dayIndex}>
            <button
              type="button"
              className={`pilule${j.dayIndex === jour.dayIndex ? ' pilule--active' : ''}`}
              aria-current={j.dayIndex === jour.dayIndex ? 'true' : undefined}
              onClick={() => {
                setJourChoisi(j.dayIndex);
                fermer();
                setJourneeAConfirmer(null);
              }}
            >
              Jour {j.dayIndex}
            </button>
          </li>
        ))}
      </ul>

      <article className="journee">
        <h3 className="journee__titre">
          Jour {jour.dayIndex}
          {jour.date && <span className="journee__date">{formaterDate(jour.date)}</span>}
          {!lectureSeule && (
            <SupprimerJournee
              jour={jour}
              confirme={journeeAConfirmer === jour.dayIndex}
              envoi={journeeEnSuppression === jour.dayIndex}
              onDemander={() => setJourneeAConfirmer(jour.dayIndex)}
              onAnnuler={() => setJourneeAConfirmer(null)}
              onSupprimer={retirerJournee(jour.dayIndex)}
            />
          )}
        </h3>

        {jour.items.length === 0 && journeeEnGeneration !== jour.dayIndex && (
          <p className="aide aide--haut">Rien de prévu ce jour-là.</p>
        )}

        <ul className="elements">
          {jour.items.map((item) => (
            <Element key={item.id} {...proprietesElement(item)} />
          ))}
        </ul>

        {journeeEnGeneration === jour.dayIndex ? (
          <p className="patiente" role="status">
            <span className="patiente__point" aria-hidden="true" />
            Nouvelle proposition en cours pour cette journée… Le reste du
            programme n’est pas touché.
          </p>
        ) : (
          !lectureSeule && (
            <div className="actions">
              <button
                className="bouton bouton--discret bouton--auto"
                type="button"
                disabled={journeeEnGeneration !== null}
                onClick={async () => {
                  setErreurRegeneration(null);
                  try {
                    onChange(await regenererJournee(tripId, jour.dayIndex));
                  } catch (echec) {
                    setErreurRegeneration(echec.message);
                  }
                }}
              >
                Proposer une autre journée
              </button>
              <p className="aide">
                L’IA repropose ce seul jour, en évitant ce qui est déjà prévu
                ailleurs. Les étapes actuelles seront remplacées.
              </p>
            </div>
          )
        )}

        {erreurRegeneration && (
          <p className="erreur" role="alert">
            {erreurRegeneration}
          </p>
        )}

        {lectureSeule ? null : ouvert === `ajout:${jour.dayIndex}` ? (
          <div className="ajout">
            <ElementForm
              mode={itineraire.mode}
              libelleAction="Ajouter"
              onSubmit={enregistrerAjout(jour.dayIndex)}
              onCancel={fermer}
            />
          </div>
        ) : (
          <button
            className="bouton bouton--discret bouton--auto bouton--ajout"
            type="button"
            onClick={() => setOuvert(`ajout:${jour.dayIndex}`)}
          >
            Ajouter une étape
          </button>
        )}
      </article>

      {erreurJournee && (
        <p className="erreur" role="alert">
          {erreurJournee}
        </p>
      )}

      {!lectureSeule && (
        <div className="ajout-journee">
          <button
            className="bouton bouton--discret bouton--auto"
            type="button"
            onClick={async () => onChange(await ajouterJournee(tripId))}
          >
            Ajouter une journée
          </button>
          <p className="aide">Le voyage sera allongé d’un jour, dates comprises.</p>
        </div>
      )}
    </section>
  );
}
